/**
 * LifetimeDeadlineBanner — slim sticky bar mounted globally above every
 * route. Ticks down to the `deadline_iso` returned by /api/billing/lifetime-stats
 * (env-driven server-side, `LIFETIME_DEADLINE_ISO`). Below that ISO it's
 * hidden; above it, it shows "£25 → £NEXT · Xd Yh Zm Ws" with a click-through
 * to /pricing.
 *
 * Dismissal is session-scoped (sessionStorage) — the banner re-appears on
 * the next browser session so returning visitors still see the deadline
 * pressure but a single-session user isn't nagged after one dismissal.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, Zap } from "lucide-react";
import { api } from "../lib/api";

const DISMISS_KEY = "stagvault_deadline_banner_dismissed";

function formatCountdown(msRemaining) {
  if (msRemaining <= 0) return null;
  const totalSec = Math.floor(msRemaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return { days, hours, mins, secs };
}

export function LifetimeDeadlineBanner() {
  const [config, setConfig] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/lifetime-stats");
        if (!cancelled) setConfig(data);
      } catch {
        /* stay silent — banner just won't render */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  useEffect(() => {
    if (dismissed || !config?.deadline_iso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [dismissed, config?.deadline_iso]);

  const deadlineMs = useMemo(() => {
    if (!config?.deadline_iso) return null;
    const t = Date.parse(config.deadline_iso);
    return Number.isFinite(t) ? t : null;
  }, [config?.deadline_iso]);

  const countdown = deadlineMs ? formatCountdown(deadlineMs - now) : null;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* noop — private mode */
    }
    setDismissed(true);
  };

  if (dismissed || !countdown || !config) return null;
  if (config.sold_out) return null;

  const currentPrice = Number(config.price || 0);
  const nextPrice = config.next_price != null ? Number(config.next_price) : null;
  const priceHook = nextPrice && nextPrice > currentPrice
    ? `£${currentPrice} → £${nextPrice}`
    : `£${currentPrice} forever`;

  // Segment style — sharp, mono, milestone-serious not carnival-hype.
  return (
    <div
      className="relative z-40 border-b border-purple-500/30 bg-gradient-to-r from-black via-[#150725] to-black text-white"
      data-testid="lifetime-deadline-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center gap-3 md:gap-6">
        <Zap className="w-4 h-4 text-amber-300 flex-shrink-0 hidden md:block" />
        <Link
          to="/pricing?utm_source=deadline_banner&utm_medium=site&utm_campaign=track100_deadline"
          className="flex-1 flex items-center gap-2 md:gap-4 flex-wrap min-w-0"
          data-testid="deadline-banner-cta"
        >
          <span className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-amber-300 font-mono flex-shrink-0">
            Lifetime closes
          </span>
          <span className="text-sm md:text-base text-zinc-100 font-medium truncate">
            <span className="text-purple-300 font-bold">{priceHook}</span>
            <span className="text-zinc-500 mx-2">·</span>
            <span className="font-mono tabular-nums" data-testid="deadline-timer">
              {countdown.days > 0 && (
                <>
                  <span className="text-white font-bold">{countdown.days}</span>
                  <span className="text-zinc-500">d </span>
                </>
              )}
              <span className="text-white font-bold">{String(countdown.hours).padStart(2, "0")}</span>
              <span className="text-zinc-500">h </span>
              <span className="text-white font-bold">{String(countdown.mins).padStart(2, "0")}</span>
              <span className="text-zinc-500">m </span>
              <span className="text-white font-bold">{String(countdown.secs).padStart(2, "0")}</span>
              <span className="text-zinc-500">s</span>
            </span>
          </span>
          <span className="hidden md:inline-flex items-center gap-1 text-purple-300 hover:text-purple-200 text-xs tracking-widest uppercase font-mono ml-auto">
            Lock £{currentPrice} →
          </span>
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors flex-shrink-0"
          aria-label="Dismiss deadline banner"
          data-testid="deadline-banner-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
